import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { MobileNetworkTopology } from './MobileNetworkTopology';
import {
  NetworkTopology3D,
  NetworkHealthMonitor,
  GeographicNetworkOverlay,
  NetworkDiscoveryVisualization,
  InteractiveNetworkAnalysis,
} from '../components/network';
import type {
  NetworkNode,
  NetworkConnection,
  NetworkHealthData,
  LatencyHeatmapData,
  BandwidthFlowData,
  GeographicNode,
  GeographicConnection,
  NetworkPartition,
  DiscoveryStrategy,
  DiscoveryEvent,
  PeerConnection,
  LoadBalancingConfig,
  MessageFlow,
  PerformanceBottleneck,
} from '../components/network';

// NetworkTopologyPageProps interface for future use
// interface NetworkTopologyPageProps {
//   // Mock props for demonstration  
// }

// Visualization modes
type VisualizationMode = '3d' | 'geographic' | 'health' | 'discovery' | 'analysis';

/**
 * Generate mock network data for demonstration
 */
function generateMockNetworkData() {
  const regions = ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1', 'ap-northeast-1'];
  const nodeTypes = ['leader', 'follower', 'candidate', 'observer', 'gateway'] as const;
  const statuses = ['healthy', 'degraded', 'unhealthy', 'offline'] as const;
  const providers = ['aws', 'gcp', 'azure', 'private'] as const;
  
  // Helper function to generate realistic cloud node names
  const generateNodeLabel = (type: string, index: number, region: string, provider: string): string => {
    // Type abbreviations for compact display
    const typeAbbrev: Record<string, string> = {
      'leader': 'lead',
      'follower': 'foll',
      'candidate': 'cand',
      'observer': 'obsv',
      'gateway': 'gate',
    };
    
    // Region abbreviations
    const regionAbbrev: Record<string, string> = {
      'us-east-1': 'use1',
      'us-west-2': 'usw2',
      'eu-west-1': 'euw1',
      'ap-south-1': 'aps1',
      'ap-northeast-1': 'apne1',
    };
    
    // Format: role-number.region.provider
    // Example: lead-01.use1.aws or foll-042.euw1.gcp
    const role = typeAbbrev[type] || type.slice(0, 4);
    const num = index.toString().padStart(2, '0');
    const reg = regionAbbrev[region] || region.slice(0, 4);
    
    return `${role}-${num}.${reg}.${provider}`;
  };

  // Generate nodes
  const nodes: NetworkNode[] = [];
  const geoNodes: GeographicNode[] = [];
  
  for (let i = 0; i < 150; i++) {
    const id = `node-${i.toString().padStart(3, '0')}`;
    const region = regions[Math.floor(Math.random() * regions.length)];
    const type = nodeTypes[Math.floor(Math.random() * nodeTypes.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const provider = providers[Math.floor(Math.random() * providers.length)];
    
    // Generate geographic coordinates based on region
    const regionCoords = {
      'us-east-1': { lat: 39.0458, lon: -76.6413 },
      'us-west-2': { lat: 45.8696, lon: -119.6880 },
      'eu-west-1': { lat: 53.4084, lon: -8.2439 },
      'ap-south-1': { lat: 19.0760, lon: 72.8777 },
      'ap-northeast-1': { lat: 35.6762, lon: 139.6503 },
    };
    
    const baseCoords = regionCoords[region as keyof typeof regionCoords];
    const lat = baseCoords.lat + (Math.random() - 0.5) * 10;
    const lon = baseCoords.lon + (Math.random() - 0.5) * 20;

    const connections: string[] = [];
    const connectionCount = Math.floor(Math.random() * 8) + 2;
    for (let j = 0; j < connectionCount; j++) {
      const targetId = `node-${Math.floor(Math.random() * 150).toString().padStart(3, '0')}`;
      if (targetId !== id && !connections.includes(targetId)) {
        connections.push(targetId);
      }
    }

    const metrics = {
      cpu: Math.random() * 100,
      memory: Math.random() * 100,
      network: Math.random() * 100,
      consensus: Math.random() * 100,
      connections: connections.length,
      messageRate: Math.random() * 1000,
      errorRate: Math.random() * 0.1,
    };

    const node: NetworkNode = {
      id,
      label: generateNodeLabel(type, i, region, provider),
      type,
      status,
      position: {
        x: (Math.random() - 0.5) * 200,
        y: (Math.random() - 0.5) * 200,
        z: (Math.random() - 0.5) * 200,
      },
      metrics,
      connections,
      lastSeen: new Date(Date.now() - Math.random() * 3600000),
      region,
      datacenter: `dc-${Math.floor(Math.random() * 5) + 1}`,
      tags: ['env-prod', `tier-${Math.floor(Math.random() * 3) + 1}`],
    };

    const geoNode: GeographicNode = {
      id,
      label: `Node ${i}`,
      latitude: lat,
      longitude: lon,
      type: type === 'leader' ? 'datacenter' : type === 'gateway' ? 'gateway' : 'edge',
      provider,
      region,
      status,
      metrics: {
        cpu: metrics.cpu,
        memory: metrics.memory,
        network: metrics.network,
        connections: connections.length,
      },
      capacity: {
        max: 1000,
        current: Math.random() * 800,
      },
    };

    nodes.push(node);
    geoNodes.push(geoNode);
  }

  // Generate connections
  const connections: NetworkConnection[] = [];
  const geoConnections: GeographicConnection[] = [];
  
  nodes.forEach(node => {
    node.connections.forEach(targetId => {
      // const _connectionId = `${node.id}-${targetId}`;
      // const _reverseId = `${targetId}-${node.id}`;
      
      // Avoid duplicate connections
      if (!connections.find(c => c.source === node.id && c.target === targetId) &&
          !connections.find(c => c.source === targetId && c.target === node.id)) {
        
        const latency = Math.random() * 200 + 10;
        const bandwidth = Math.random() * 1000;
        const status = Math.random() > 0.1 ? 'active' : Math.random() > 0.5 ? 'degraded' : 'failed';
        
        const connection: NetworkConnection = {
          id: `conn-${node.id}-${targetId}`,
          source: node.id,
          target: targetId,
          latency,
          bandwidth,
          status,
          messageRate: Math.random() * 100,
          direction: 'bidirectional',
        };

        const geoConnection: GeographicConnection = {
          source: node.id,
          target: targetId,
          latency,
          bandwidth,
          status,
          type: Math.random() > 0.7 ? 'private' : 'internet',
          messageRate: Math.random() * 100,
        };

        connections.push(connection);
        geoConnections.push(geoConnection);
      }
    });
  });

  // Generate health data
  const healthData: NetworkHealthData[] = nodes.map(node => ({
    nodeId: node.id,
    timestamp: new Date(),
    metrics: {
      rtt: Math.random() * 100,
      latency: Math.random() * 200,
      bandwidth: Math.random() * 1000,
      packetLoss: Math.random() * 0.05,
      jitter: Math.random() * 50,
      connectionQuality: Math.random(),
    },
    connectionState: Math.random() > 0.9 ? 'connecting' : 
                    Math.random() > 0.95 ? 'failed' : 'connected',
    errors: Math.floor(Math.random() * 10),
    region: node.region,
  }));

  // Generate latency heatmap data
  const latencyHeatmap: LatencyHeatmapData[] = [];
  regions.forEach(sourceRegion => {
    regions.forEach(targetRegion => {
      if (sourceRegion !== targetRegion) {
        latencyHeatmap.push({
          sourceRegion,
          targetRegion,
          avgLatency: Math.random() * 200 + 20,
          p95Latency: Math.random() * 300 + 50,
          p99Latency: Math.random() * 500 + 100,
          samples: Math.floor(Math.random() * 1000) + 100,
          timestamp: new Date(),
        });
      }
    });
  });

  // Generate bandwidth flows
  const bandwidthFlows: BandwidthFlowData[] = [];
  for (let i = 0; i < 50; i++) {
    const source = nodes[Math.floor(Math.random() * nodes.length)];
    const target = nodes[Math.floor(Math.random() * nodes.length)];
    
    if (source.id !== target.id) {
      const inbound = Math.random() * 1024 * 1024 * 100; // Up to 100MB/s
      const outbound = Math.random() * 1024 * 1024 * 100;
      
      bandwidthFlows.push({
        source: source.id,
        target: target.id,
        inbound,
        outbound,
        total: inbound + outbound,
        direction: 'bidirectional',
        protocol: Math.random() > 0.6 ? 'tcp' : Math.random() > 0.3 ? 'udp' : 'websocket',
      });
    }
  }

  // Generate discovery strategies
  const strategies: DiscoveryStrategy[] = [
    {
      type: 'multicast',
      enabled: true,
      config: { interval: 5000, timeout: 2000, retries: 3 },
      metrics: {
        nodesDiscovered: Math.floor(Math.random() * 50) + 20,
        successRate: 0.8 + Math.random() * 0.2,
        averageLatency: Math.random() * 50 + 10,
        bandwidth: Math.random() * 1024 * 10,
      },
    },
    {
      type: 'DHT',
      enabled: true,
      config: { interval: 10000, timeout: 5000, retries: 2 },
      metrics: {
        nodesDiscovered: Math.floor(Math.random() * 100) + 50,
        successRate: 0.9 + Math.random() * 0.1,
        averageLatency: Math.random() * 100 + 20,
        bandwidth: Math.random() * 1024 * 20,
      },
    },
    {
      type: 'gossip',
      enabled: true,
      config: { interval: 1000, timeout: 1000, retries: 1 },
      metrics: {
        nodesDiscovered: Math.floor(Math.random() * 30) + 10,
        successRate: 0.7 + Math.random() * 0.3,
        averageLatency: Math.random() * 30 + 5,
        bandwidth: Math.random() * 1024 * 5,
      },
    },
  ];

  // Generate discovery events
  const discoveryEvents: DiscoveryEvent[] = [];
  for (let i = 0; i < 200; i++) {
    const sourceNode = nodes[Math.floor(Math.random() * nodes.length)];
    const targetNode = nodes[Math.floor(Math.random() * nodes.length)];
    const strategy = strategies[Math.floor(Math.random() * strategies.length)];
    
    discoveryEvents.push({
      id: `event-${i}`,
      timestamp: new Date(Date.now() - Math.random() * 3600000),
      type: Math.random() > 0.5 ? 'node_discovered' : 'heartbeat',
      sourceNode: sourceNode.id,
      targetNode: targetNode.id !== sourceNode.id ? targetNode.id : undefined,
      strategy: strategy.type,
      latency: Math.random() * 100,
      success: Math.random() > 0.1,
    });
  }

  // Generate peer connections
  const peerConnections: PeerConnection[] = [];
  for (let i = 0; i < 80; i++) {
    const source = nodes[Math.floor(Math.random() * nodes.length)];
    const target = nodes[Math.floor(Math.random() * nodes.length)];
    
    if (source.id !== target.id) {
      peerConnections.push({
        id: `conn-${i}`,
        sourceNode: source.id,
        targetNode: target.id,
        status: Math.random() > 0.8 ? 'establishing' : 
                Math.random() > 0.9 ? 'failed' : 'established',
        establishedAt: new Date(Date.now() - Math.random() * 3600000),
        lastSeen: new Date(),
        route: [source.id, target.id],
        metrics: {
          rtt: Math.random() * 100,
          bandwidth: Math.random() * 1000,
          reliability: Math.random(),
        },
        strategy: strategies[Math.floor(Math.random() * strategies.length)].type,
      });
    }
  }

  // Generate load balancing config
  const loadBalancing: LoadBalancingConfig = {
    algorithm: 'least_connections',
    enabled: true,
    metrics: {
      requestsDistributed: Math.floor(Math.random() * 10000) + 1000,
      averageResponseTime: Math.random() * 100 + 20,
      nodesInRotation: Math.floor(Math.random() * 20) + 10,
    },
  };

  // Generate message flows
  const messageFlows: MessageFlow[] = [];
  for (let i = 0; i < 500; i++) {
    const source = nodes[Math.floor(Math.random() * nodes.length)];
    const target = nodes[Math.floor(Math.random() * nodes.length)];
    
    if (source.id !== target.id) {
      const messageType = Math.random() > 0.5 ? 'consensus' : 'heartbeat';
      messageFlows.push({
        id: `flow-${i}`,
        source: source.id,
        target: target.id,
        type: messageType,
        messageType,
        status: 'delivered',
        payload: Math.floor(Math.random() * 10000) + 100,
        timestamp: Date.now() - Math.random() * 3600000,
        latency: Math.random() * 200,
        success: Math.random() > 0.05,
        route: [source.id, target.id],
      });
    }
  }

  // Generate performance bottlenecks
  const bottlenecks: PerformanceBottleneck[] = [];
  for (let i = 0; i < 10; i++) {
    const node = nodes[Math.floor(Math.random() * nodes.length)];
    const type = ['cpu', 'memory', 'network', 'consensus'][Math.floor(Math.random() * 4)] as any;
    
    bottlenecks.push({
      id: `bottleneck-${i}`,
      nodeId: node.id,
      type,
      severity: Math.random() > 0.7 ? 'critical' : Math.random() > 0.5 ? 'high' : 'medium',
      value: Math.random() * 100,
      threshold: 80,
    });
  }

  // Generate network partitions
  const partitions: NetworkPartition[] = [];
  if (Math.random() > 0.7) { // 30% chance of active partition
    const partitionNodes = nodes.slice(0, 10).map(n => n.id);
    partitions.push({
      id: 'partition-1',
      nodes: partitionNodes,
      affectedNodes: partitionNodes,
      affectedRegions: ['us-east-1'],
      size: partitionNodes.length,
      detectedAt: new Date(Date.now() - 300000).toISOString(),
      lastSeen: new Date().toISOString(),
      isolatedFrom: nodes.slice(10, 15).map(n => n.id),
      healingAttempts: 0,
      status: 'active',
      type: 'network-split',
      startTime: new Date(Date.now() - 300000).toISOString(),
      severity: 'high',
      cause: 'Network connectivity issue',
      metadata: {
        affectedServices: ['consensus', 'replication'],
        estimatedDowntime: 5,
        businessImpact: 'Degraded write availability in us-east-1',
      },
    });
  }

  return {
    nodes,
    connections,
    geoNodes,
    geoConnections,
    healthData,
    latencyHeatmap,
    bandwidthFlows,
    strategies,
    discoveryEvents,
    peerConnections,
    loadBalancing,
    messageFlows,
    bottlenecks,
    partitions,
  };
}

/**
 * Main Network Topology page component
 */
export function NetworkTopologyPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [searchParams, setSearchParams] = useSearchParams();

  // Detect mobile screens
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Render mobile version for small screens
  if (isMobile) {
    return <MobileNetworkTopology />;
  }
  
  // Get initial visualization mode from URL or default to '3d'
  const initialMode = useMemo(() => {
    const mode = searchParams.get('view');
    if (mode && ['3d', 'geographic', 'health', 'discovery', 'analysis'].includes(mode)) {
      return mode as VisualizationMode;
    }
    return '3d';
  }, [searchParams]);
  
  const [visualizationMode, setVisualizationMode] = useState<VisualizationMode>(initialMode);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | undefined>();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval] = useState(5000);
  // Skip intro if we're starting on a non-3D view
  const [hasShownIntro, setHasShownIntro] = useState(initialMode !== '3d');
  
  // Update URL when visualization mode changes
  const handleModeChange = useCallback((mode: VisualizationMode) => {
    setVisualizationMode(mode);
    setSearchParams({ view: mode });
  }, [setSearchParams]);

  // Generate mock data
  const mockData = useMemo(() => generateMockNetworkData(), []);

  // Auto-refresh simulation
  useEffect(() => {
    if (!autoRefresh) {
return;
}

    const interval = setInterval(() => {
      // In a real implementation, this would fetch fresh data
      // For demo purposes, we'll just trigger a re-render
      console.log('Auto-refreshing network data...');
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);
  
  // Mark intro as shown after first 3D view
  useEffect(() => {
    if (visualizationMode === '3d' && !hasShownIntro) {
      // Set a timeout to mark intro as shown after it would have completed
      const timer = setTimeout(() => {
        setHasShownIntro(true);
      }, 3500); // Match the intro duration
      
      return () => clearTimeout(timer);
    }
  }, [visualizationMode, hasShownIntro]);

  const handleNodeSelect = useCallback((nodeId: string) => {
    setSelectedNode(nodeId);
    if (!selectedNodes.includes(nodeId)) {
      setSelectedNodes(prev => [...prev, nodeId]);
    }
  }, [selectedNodes]);

  const handleConnectionSelect = useCallback((connection: any) => {
    console.log('Connection selected:', connection);
  }, []);

  const handleStrategyToggle = useCallback((strategyType: string, enabled: boolean) => {
    console.log(`Strategy ${strategyType} ${enabled ? 'enabled' : 'disabled'}`);
  }, []);

  const handleAnalysisRun = useCallback(async (_analysisType: string, _params: any): Promise<any> => {
    // Mock analysis results
    return {
      id: `analysis-${Date.now()}`,
      type: _analysisType,
      status: 'complete',
      findings: [],
      timestamp: new Date(),
      summary: 'Analysis complete',
      criticalPath: selectedNodes.slice(0, 5),
      bottlenecks: mockData.bottlenecks.filter(b => selectedNodes.includes(b.nodeId)),
      partitions: mockData.partitions,
      messageFlows: mockData.messageFlows.filter(f =>
        selectedNodes.includes(f.source) || selectedNodes.includes(f.target),
      ),
      networkHealth: {
        score: 0.85,
        latency: 12.5,
        throughput: 1024,
        packetLoss: 0.02,
        overall: 0.85,
        connectivity: 0.92,
        performance: 0.78,
        reliability: 0.89,
      },
    };
  }, [selectedNodes, mockData]);

  const timeRange = useMemo(() => ({
    start: new Date(Date.now() - 3600000), // 1 hour ago
    end: new Date(),
  }), []);

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl suse-bold text-white">Network Topology</h1>
        
        <div className="flex items-center gap-4">
          {/* Auto-refresh toggle */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300 text-sm">Auto-refresh</span>
          </label>

          {/* Visualization mode selector - scrollable on mobile */}
          <div className="flex bg-gray-800 rounded-lg p-1 overflow-x-auto tab-list">
            {[
              { mode: '3d', label: '3D View' },
              { mode: 'geographic', label: 'Geographic' },
              { mode: 'health', label: 'Health' },
              { mode: 'discovery', label: 'Discovery' },
              { mode: 'analysis', label: 'Analysis' },
            ].map(({ mode, label }) => (
              <button
                key={mode}
                onClick={() => handleModeChange(mode as VisualizationMode)}
                className={`px-3 py-1 rounded text-sm transition-colors whitespace-nowrap tab-button ${
                  visualizationMode === mode
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats summary */}
      <div className="dashboard-grid">
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary-400">{mockData.nodes.length}</div>
          <div className="text-sm text-gray-400">Total Nodes</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="text-2xl font-bold text-status-online">
            {mockData.nodes.filter(n => n.status === 'healthy').length}
          </div>
          <div className="text-sm text-gray-400">Healthy Nodes</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="text-2xl font-bold text-warning-400">{mockData.connections.length}</div>
          <div className="text-sm text-gray-400">Active Connections</div>
        </div>
        <div className="bg-gray-900 rounded-lg p-4">
          <div className="text-2xl font-bold text-secondary-400">{mockData.partitions.length}</div>
          <div className="text-sm text-gray-400">Active Partitions</div>
        </div>
      </div>

      {/* Main visualization */}
      <AnimatePresence mode="wait">
        <motion.div
          key={visualizationMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="min-h-[400px] md:min-h-[600px] lg:min-h-[800px]"
        >
          {visualizationMode === '3d' && (
            <div className="bg-gray-900 rounded-lg p-4 h-[400px] md:h-[600px] lg:h-[800px] relative overflow-hidden visualization-container network-3d-container">
              <NetworkTopology3D
                _nodes={mockData.nodes}
                connections={mockData.connections}
                selectedNodeId={selectedNode}
                onNodeSelect={handleNodeSelect}
                onConnectionSelect={handleConnectionSelect}
                showLabels
                showMetrics
                enablePhysics
                maxNodes={1000}
                skipIntro={hasShownIntro}
              />
            </div>
          )}

          {visualizationMode === 'geographic' && (
            <GeographicNetworkOverlay
              _nodes={mockData.geoNodes}
              connections={mockData.geoConnections}
              partitions={mockData.partitions}
              selectedNode={selectedNode}
              onNodeSelect={handleNodeSelect}
              onConnectionSelect={handleConnectionSelect}
              showLatencyLines
              showRegionalClusters
              width={1200}
              height={600}
            />
          )}

          {visualizationMode === 'health' && (
            <NetworkHealthMonitor
              healthData={mockData.healthData}
              latencyHeatmap={mockData.latencyHeatmap}
              bandwidthFlows={mockData.bandwidthFlows}
              selectedNode={selectedNode}
              timeRange={timeRange}
              onNodeSelect={handleNodeSelect}
              refreshInterval={refreshInterval}
            />
          )}

          {visualizationMode === 'discovery' && (
            <NetworkDiscoveryVisualization
              strategies={mockData.strategies}
              discoveryEvents={mockData.discoveryEvents}
              peerConnections={mockData.peerConnections}
              loadBalancing={mockData.loadBalancing}
              selectedNode={selectedNode}
              timeRange={timeRange}
              onStrategyToggle={handleStrategyToggle}
              onNodeSelect={handleNodeSelect}
            />
          )}

          {visualizationMode === 'analysis' && (
            <InteractiveNetworkAnalysis
              _nodes={mockData.nodes}
              messageFlows={mockData.messageFlows}
              partitions={mockData.partitions}
              bottlenecks={mockData.bottlenecks}
              selectedNodes={selectedNodes}
              onNodeSelection={setSelectedNodes}
              onAnalysisRun={handleAnalysisRun}
              realTimeMode={autoRefresh}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Selected node info panel */}
      {selectedNode && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-900 rounded-lg p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Selected Node Details</h3>
            <button
              onClick={() => setSelectedNode(undefined)}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          
          {(() => {
            const node = mockData.nodes.find(n => n.id === selectedNode);
            if (!node) {
return <div className="text-gray-400">Node not found</div>;
}
            
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <h4 className="font-medium text-white mb-2">Basic Info</h4>
                  <div className="space-y-1 text-sm">
                    <div>ID: <span className="text-gray-400 font-mono">{node.id}</span></div>
                    <div>Label: <span className="text-primary-400">{node.label}</span></div>
                    <div>Type: <span className="text-warning-400">{node.type}</span></div>
                    <div>Status: <span className={`${
                      node.status === 'healthy' ? 'text-status-online' :
                      node.status === 'degraded' ? 'text-warning-400' :
                      'text-danger-400'
                    }`}>{node.status}</span></div>
                    <div>Region: <span className="text-primary-400">{node.region}</span></div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-white mb-2">Metrics</h4>
                  <div className="space-y-1 text-sm">
                    <div>CPU: <span className="text-primary-400">{node.metrics.cpu.toFixed(1)}%</span></div>
                    <div>Memory: <span className="text-status-online">{node.metrics.memory.toFixed(1)}%</span></div>
                    <div>Network: <span className="text-warning-400">{node.metrics.network.toFixed(1)}%</span></div>
                    <div>Consensus: <span className="text-secondary-400">{node.metrics.consensus.toFixed(1)}%</span></div>
                    <div>Connections: <span className="text-orange-400">{node.metrics.connections}</span></div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-white mb-2">Connections</h4>
                  <div className="text-sm">
                    <div className="mb-2">Count: <span className="text-primary-400">{node.connections.length}</span></div>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {node.connections.slice(0, 10).map(connId => (
                        <div key={connId} className="text-gray-400 font-mono text-xs">
                          {connId}
                        </div>
                      ))}
                      {node.connections.length > 10 && (
                        <div className="text-gray-500 text-xs">
                          +{node.connections.length - 10} more...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </motion.div>
      )}
    </div>
  );
}

export default NetworkTopologyPage;